import React, { useContext, useEffect, useState } from 'react'
import i18n from 'simple-react-i18n'
import PropTypes from 'prop-types'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { template, toUpper } from 'lodash'
import { Card, CardContent, Grid, Stack, Typography } from '@mui/material'
import useDebounce from 'utils/customHook/useDebounce'
import { getGeneralLevelling } from 'utils/SettingUtils'
import GlobalParametersDto from 'administration/dto/GlobalParameterDto'
import moment from 'moment'
import { MODULES, OBSERVATORY, WIDGET, WIDGET_CARTO, WIDGET_STATION } from './ObservatoryConstants'
import { ModuleHabsContext, ModuleHabsDispatchContext } from './AdminModulesApp'
import { FieldsetMUI } from 'components/StyledElements'
import ShowChartRoundedIcon from '@mui/icons-material/ShowChartRounded'
import MultilineChartRoundedIcon from '@mui/icons-material/MultilineChartRounded'
import InsertChartOutlinedRoundedIcon from '@mui/icons-material/InsertChartOutlinedRounded'
import PieChartRoundedIcon from '@mui/icons-material/PieChartRounded'
import BuildRoundedIcon from '@mui/icons-material/BuildRounded'
import { H_DISTRIBUTION_MODULE, H_HYDRO_MODULE, H_INSTALLATION_MODULE, H_PIEZO_MODULE, H_PLUVIO_MODULE, H_PRODUCTION_MODULE, H_QUALITO_MODULE } from 'account/constants/AccessRulesConstants'
import HabilitationRequired from 'utils/components/HabilitationRequired'
import noData from 'assets/pictures/observatory/no-data.svg'
import PiezometryAction from 'piezometry/actions/PiezometryAction'
import HydrometryAction from 'hydrometry/actions/HydrometryAction'
import PluviometryAction from 'pluviometry/actions/PluviometryAction'
import ShowDataTypesRules from './components/ShowDataTypesRules'
import ParameterCard from './components/ParameterCard'
import ParameterWithSwitch from './components/inputs/ParameterWithSwitch'
import WithStats from './components/WithStats'
import LoginPageParameters from './components/parameters/LoginPageParameters'
import WidgetParameters from './components/parameters/WidgetParameters'
import PasswordParameters from './components/parameters/PasswordParameters'
import CartographyParameters from './components/parameters/CartographyParameters'
import DocumentsParameters from './components/parameters/DocumentsParameters'
import SupportPanelParameters from './components/parameters/SupportPanelParameters'
import PiezometryParameters from './components/parameters/PiezometryParameters'
import HydrometryParameters from './components/parameters/HydrometryParameters'
import PluviometryParameters from './components/parameters/PluviometryParameters'
import ProductionUnitParameters from './components/parameters/ProductionUnitParameters'
import DistributionUnitParameters from './components/parameters/DistributionUnitParameters'

const OTHER_PARAMETERS = ['passwordPolicy', 'securityPasswordDescription', 'supportMailAddress', 'supportPhoneNumber', 'supportContactSchedule', 'managerUrl', 'UDI_REFERENCE_THRESHOLD', 'contributorTypeOperator', 'contributorTypeAdministrator', 'PROD_REFERENCE_THRESHOLD']
const DEFAULT_MODULE = 'SIEAU'
const CMS_CATEGORIES_TO_IGNORE = [-1]
const MODULES_WITH_ACCOUNT_PARAMETERS = [MODULES.find(m => m.id === WIDGET.id)?.name]

const CustomizationParameters = ({
    module = {},
}) => {
    const dispatch = useDispatch()
    const {
        globalParameters,
        accountUser,
        cmsEvents,
        applicativeUserSettings,
        user,
        piezometryDataTypes,
        hydrometryDataTypes,
        pluviometryDataTypes,
    } = useSelector(store => ({
        globalParameters: store.AdministrationReducer.globalParameters,
        accountUser: store.AccountReducer.accountUser,
        cmsEvents: store.EventsReducer.cmsEvents,
        applicativeUserSettings: store.AdministrationReducer.applicativeUserSettings,
        user: store.UserReducer.user,
        piezometryDataTypes: store.PiezometryReducer.piezometryDataTypes,
        hydrometryDataTypes: store.HydrometryReducer.hydrometryDataTypes,
        pluviometryDataTypes: store.PluviometryReducer.pluviometryDataTypes,
    }), shallowEqual)

    const moduleParams = useContext(ModuleHabsContext)
    const moduleParamsDispatch = useContext(ModuleHabsDispatchContext)

    const isGlobal = module.global

    const globalModuleParameters = [
        ...globalParameters.filter(gp => !isGlobal
            ? gp.module === toUpper(module.module)
            : gp.module === toUpper(OBSERVATORY.name) || gp.module === toUpper(WIDGET.name) || gp.module === toUpper(WIDGET_CARTO.name)),
        ...globalParameters.filter(gp => OTHER_PARAMETERS.includes(gp.parameter)),
    ]

    useEffect(()=> {
        if (!piezometryDataTypes.length) {
            dispatch(PiezometryAction.fetchPiezometryDataTypes())
        }
        if (!hydrometryDataTypes.length) {
            dispatch(HydrometryAction.fetchHydrometryDataTypes())
        }
        if (!pluviometryDataTypes.length) {
            dispatch(PluviometryAction.fetchPluviometryDataTypes())
        }
    }, [])

    const [parameters, setParameters] = useState(moduleParams.parameters.length ? moduleParams.parameters : globalModuleParameters)
    const [moduleParameters, setModuleParameters] = useState(moduleParams.moduleParameters.length ? moduleParams.moduleParameters : applicativeUserSettings.filter(aus => aus.login === module.login))

    useDebounce(() => {
        moduleParamsDispatch({ type: 'parameters', payload: parameters })
    }, 100, [parameters])

    useDebounce(() => {
        moduleParamsDispatch({ type: 'moduleParameters', payload: moduleParameters })
    }, 100, [moduleParameters])

    const getGlobalParameter = (parameter, value) => {
        const foundParam = parameters.find(p => p.parameter === parameter)
        return {
            ...new GlobalParametersDto({
                module: !OTHER_PARAMETERS.includes(parameter) ? toUpper(module.module) : (foundParam?.module || DEFAULT_MODULE),
                parameter,
                value: value || undefined,
                updateDate: moment().valueOf(),
                updateLogin: accountUser.login,
            }),
            isNew: !foundParam?.value,
        }
    }

    const getGlobalModuleParameter = (parameter, value) => {
        const foundParam = moduleParameters.find(p => p.parameter === parameter)
        return {
            login: module.login,
            parameter,
            value: value || undefined,
            updateDate: moment().valueOf(),
            isNew: !foundParam?.value,
        }
    }

    const filteredCmsEvents = cmsEvents.filter(ce => !CMS_CATEGORIES_TO_IGNORE.includes(ce.idCategory))
    const defaultProps = {
        parameters,
        setParameters,
        getGlobalParameter,
        moduleParameters,
        setModuleParameters,
        getGlobalModuleParameter,
        cmsEvents: filteredCmsEvents,
        displayModuleParameter: MODULES_WITH_ACCOUNT_PARAMETERS.includes(module.module),
        module,
        isGlobal,
    }

    const defaultHabsProps = { user, userHabs: moduleParams?.habilitationsUser }

    return (
        <Grid size={12}>
            <Stack spacing={1} sx={{ height: '100%' }}>
                {!isGlobal && (
                    [OBSERVATORY.name, WIDGET.name, WIDGET_CARTO.name].includes(module.module) ? (
                        <Typography variant='sectionTitle'>{i18n.adminModuleCustomizationSubtitle}</Typography>
                    ) : (
                        <Typography variant='sectionTitle'>{i18n.adminNoModuleCustomizationSubtitle}</Typography>
                    )
                )}
                {(module.module === OBSERVATORY.name || isGlobal) && (
                    <>
                        <LoginPageParameters
                            defaultProps={defaultProps}
                            isGlobal={isGlobal}
                        />
                        <WidgetParameters
                            defaultProps={defaultProps}
                            isGlobal={isGlobal}
                        />
                        <PasswordParameters
                            defaultProps={defaultProps}
                            isGlobal={isGlobal}
                        />
                        <CartographyParameters
                            defaultProps={defaultProps}
                            isGlobal={isGlobal}
                        />
                        <DocumentsParameters
                            defaultProps={defaultProps}
                            isGlobal={isGlobal}
                        />
                        <SupportPanelParameters
                            defaultProps={defaultProps}
                            isGlobal={isGlobal}
                        />
                        <ParameterCard title={i18n.datas} isGlobal={isGlobal}>
                            <WithStats {...defaultProps} />
                            <HabilitationRequired habilitation={H_PIEZO_MODULE} {...defaultHabsProps}>
                                <FieldsetMUI
                                    title={(
                                        <Stack direction='row' alignItems='center' gap={0.5} sx={{ padding: '0 0.5rem' }}>
                                            <ShowChartRoundedIcon />
                                            {i18n.piezometry}
                                        </Stack>
                                    )}
                                >
                                    <PiezometryParameters
                                        defaultProps={defaultProps}
                                        isGlobal={isGlobal}
                                    />
                                </FieldsetMUI>
                            </HabilitationRequired>
                            <HabilitationRequired habilitation={H_HYDRO_MODULE} { ...defaultHabsProps }>
                                <FieldsetMUI
                                    title={(
                                        <Stack direction='row' alignItems='center' gap={0.5} sx={{ padding: '0 0.5rem' }}>
                                            <MultilineChartRoundedIcon />
                                            {i18n.hydrometry}
                                        </Stack>
                                    )}
                                >
                                    <HydrometryParameters
                                        defaultProps={defaultProps}
                                        isGlobal={isGlobal}
                                    />
                                </FieldsetMUI>
                            </HabilitationRequired>
                            <HabilitationRequired habilitation={H_PLUVIO_MODULE} { ...defaultHabsProps }>
                                <FieldsetMUI
                                    title={(
                                        <Stack direction='row' alignItems='center' gap={0.5} sx={{ padding: '0 0.5rem' }}>
                                            <InsertChartOutlinedRoundedIcon />
                                            {i18n.pluviometry}
                                        </Stack>
                                    )}
                                >
                                    <PluviometryParameters
                                        defaultProps={defaultProps}
                                        isGlobal={isGlobal}
                                    />
                                </FieldsetMUI>
                            </HabilitationRequired>
                            <HabilitationRequired habilitation={H_QUALITO_MODULE} { ...defaultHabsProps }>
                                <FieldsetMUI
                                    title={(
                                        <Stack direction='row' alignItems='center' gap={0.5} sx={{ padding: '0 0.5rem' }}>
                                            <PieChartRoundedIcon />
                                            {i18n.quality}
                                        </Stack>
                                    )}
                                >
                                    <Stack spacing={1.25}>
                                        <ShowDataTypesRules link='quality/qualityOptions/generalOptions' {...defaultProps} />
                                    </Stack>
                                </FieldsetMUI>
                            </HabilitationRequired>
                            <HabilitationRequired habilitation={H_PRODUCTION_MODULE} { ...defaultHabsProps }>
                                <ProductionUnitParameters
                                    defaultProps={defaultProps}
                                />
                            </HabilitationRequired>
                            <HabilitationRequired habilitation={H_DISTRIBUTION_MODULE} { ...defaultHabsProps }>
                                <DistributionUnitParameters
                                    defaultProps={defaultProps}
                                />
                            </HabilitationRequired>
                            <HabilitationRequired habilitation={H_INSTALLATION_MODULE} { ...defaultHabsProps }>
                                <FieldsetMUI
                                    title={(
                                        <Stack direction='row' alignItems='center' gap={0.5} sx={{ padding: '0 0.5rem' }}>
                                            <BuildRoundedIcon />
                                            {i18n.installation}
                                        </Stack>
                                    )}
                                >
                                    <ShowDataTypesRules link='installation/installationOptions' {...defaultProps} />
                                </FieldsetMUI>
                            </HabilitationRequired>
                        </ParameterCard>
                    </>
                )}
                {[WIDGET.name, WIDGET_CARTO.name].includes(module.module) && (
                    <>
                        <CartographyParameters
                            defaultProps={defaultProps}
                            isGlobal={isGlobal}
                        />
                        <ParameterCard title={i18n.datas}>
                            <WithStats {...defaultProps} isWidget />
                            <ParameterWithSwitch
                                {...defaultProps}
                                parameter={`defaultNGF${module.module}`}
                                title={template(i18n.defaultNGFDisplay)({ nG: getGeneralLevelling() })}
                            />
                            <HabilitationRequired habilitation={H_PIEZO_MODULE} { ...defaultHabsProps }>
                                <FieldsetMUI
                                    title={(
                                        <Stack direction='row' alignItems='center' gap={0.5} sx={{ padding: '0 0.5rem' }}>
                                            <ShowChartRoundedIcon />
                                            {i18n.piezometry}
                                        </Stack>
                                    )}
                                >
                                    <PiezometryParameters
                                        defaultProps={defaultProps}
                                        isGlobal={isGlobal}
                                        isWidget
                                    />
                                </FieldsetMUI>
                            </HabilitationRequired>
                            <HabilitationRequired habilitation={H_HYDRO_MODULE} { ...defaultHabsProps }>
                                <FieldsetMUI
                                    title={(
                                        <Stack direction='row' alignItems='center' gap={0.5} sx={{ padding: '0 0.5rem' }}>
                                            <MultilineChartRoundedIcon />
                                            {i18n.hydrometry}
                                        </Stack>
                                    )}
                                >
                                    <HydrometryParameters
                                        defaultProps={defaultProps}
                                        isGlobal={isGlobal}
                                        isWidget
                                    />
                                </FieldsetMUI>
                            </HabilitationRequired>
                            <HabilitationRequired habilitation={H_PLUVIO_MODULE} { ...defaultHabsProps }>
                                <FieldsetMUI
                                    title={(
                                        <Stack direction='row' alignItems='center' gap={0.5} sx={{ padding: '0 0.5rem' }}>
                                            <InsertChartOutlinedRoundedIcon />
                                            {i18n.pluviometry}
                                        </Stack>
                                    )}
                                >
                                    <PluviometryParameters
                                        defaultProps={defaultProps}
                                        isGlobal={isGlobal}
                                        isWidget
                                    />
                                </FieldsetMUI>
                            </HabilitationRequired>
                        </ParameterCard>
                    </>
                )}
                {[WIDGET_STATION.name].includes(module.module) && (
                    <Card variant='newCard' sx={{ flex: 1, overflow: 'auto', height: '100%', textAlign: 'center' }}>
                        <CardContent sx={{ height: '100%', '& object': { height: '100%', width: 'auto' } }}>
                            <object data={noData} type='image/svg+xml' />
                        </CardContent>
                    </Card>
                )}
            </Stack>
        </Grid>
    )
}
CustomizationParameters.propTypes = {
    module: PropTypes.shape({
        global: PropTypes.bool,
        login: PropTypes.string,
        module: PropTypes.string,
        moduleTypeId: PropTypes.number,
    }),
}

export default CustomizationParameters
